import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { User, Building, Save, LogOut, Mail, Phone, MapPin, Calendar, Shield, Palmtree, Plus, Trash2, Edit2, BookOpen, Bell, Upload, X, Image as ImageIcon, FileText, RotateCcw, Loader2, ChevronDown, ChevronRight, Receipt, HeartPulse, Euro, Users, Smartphone } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

export const Settings = () => {
  const { user, updateProfile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clubSettings, setClubSettings] = useState(null);
  
  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Vacances state
  const [vacances, setVacances] = useState([]);
  const [showVacancesModal, setShowVacancesModal] = useState(false);
  const [editingVacances, setEditingVacances] = useState(null);
  const [newVacances, setNewVacances] = useState({
    nom: '',
    date_debut: '',
    date_fin: '',
    saison: '',
    notes: ''
  });
  
  // Themes state
  const [themes, setThemes] = useState([]);
  const [showThemesModal, setShowThemesModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [newTheme, setNewTheme] = useState({
    nom: '',
    description: '',
    couleur: '#3b82f6'
  });
  
  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({});
  const [notificationDomains, setNotificationDomains] = useState({});
  const [notificationActionLabels, setNotificationActionLabels] = useState({});
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  
  // Age categories state
  const [ageCategories, setAgeCategories] = useState(null);
  const [editingAgeCategories, setEditingAgeCategories] = useState(null);
  const [savingAgeCategories, setSavingAgeCategories] = useState(false);
  
  // Accounting year start date state
  const [anneeComptable, setAnneeComptable] = useState({ mois: 1, jour: 1 });
  const [isDefaultAnneeComptable, setIsDefaultAnneeComptable] = useState(true);
  const [savingAnneeComptable, setSavingAnneeComptable] = useState(false);
  
  // CGU state
  const [cguContent, setCguContent] = useState('');
  const [cguVersion, setCguVersion] = useState('1.0');
  const [cguUpdatedAt, setCguUpdatedAt] = useState(null);
  const [cguIsDefault, setCguIsDefault] = useState(true);
  const [loadingCgu, setLoadingCgu] = useState(false);
  const [savingCgu, setSavingCgu] = useState(false);
  
  const [profileData, setProfileData] = useState({
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    pseudo: user?.pseudo || '',
    telephone: user?.telephone || '',
    adresse: user?.adresse || '',
    date_naissance: user?.date_naissance || ''
  });

  useEffect(() => {
    fetchNotificationPreferences();
    if (isAdmin()) {
      fetchClubSettings();
      fetchVacances();
      fetchThemes();
      fetchAgeCategories();
      fetchAnneeComptable();
      fetchCgu();
    }
  }, []);

  const fetchNotificationPreferences = async () => {
    setLoadingNotifications(true);
    try {
      const response = await axios.get(`${API_URL}/api/notifications/preferences`);
      setNotificationPrefs(response.data.preferences || {});
      setNotificationDomains(response.data.domains || {});
      setNotificationActionLabels(response.data.action_labels || {});
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleSaveNotificationPrefs = async () => {
    setLoadingNotifications(true);
    try {
      await axios.put(`${API_URL}/api/notifications/preferences`, notificationPrefs);
      toast.success('Préférences de notification mises à jour');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la mise à jour'));
    } finally {
      setLoadingNotifications(false);
    }
  };

  const toggleDomainExpanded = (domain) => {
    setExpandedDomains(prev => ({ ...prev, [domain]: !prev[domain] }));
  };

  const toggleActionPref = (domain, action, channel, checked) => {
    setNotificationPrefs(prev => {
      const updated = JSON.parse(JSON.stringify(prev)); // Deep clone
      if (!updated[domain]) updated[domain] = {};
      if (!updated[domain][action]) updated[domain][action] = { in_app: true, email: false };
      updated[domain][action][channel] = checked;
      return updated;
    });
  };

  const fetchClubSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/club`);
      setClubSettings(response.data);
    } catch (error) {
      console.error('Error fetching club settings:', error);
    }
  };

  const fetchVacances = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vacances`);
      setVacances(response.data);
    } catch (error) {
      console.error('Error fetching vacances:', error);
    }
  };

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/themes`);
      setThemes(response.data);
    } catch (error) {
      console.error('Error fetching themes:', error);
    }
  };

  // ============ AGE CATEGORIES ============
  const fetchAgeCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/club/age-categories`);
      setAgeCategories(response.data.categories);
      setEditingAgeCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching age categories:', error);
    }
  };

  const handleSaveAgeCategories = async () => {
    setSavingAgeCategories(true);
    try {
      await axios.put(`${API_URL}/api/club/age-categories`, {
        categories: editingAgeCategories
      });
      setAgeCategories(editingAgeCategories);
      toast.success('Catégories d\'âge mises à jour');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSavingAgeCategories(false);
    }
  };

  const handleResetAgeCategories = async () => {
    setSavingAgeCategories(true);
    try {
      const response = await axios.post(`${API_URL}/api/club/age-categories/reset`);
      setAgeCategories(response.data.categories);
      setEditingAgeCategories(response.data.categories);
      toast.success('Catégories réinitialisées aux valeurs par défaut');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la réinitialisation'));
    } finally {
      setSavingAgeCategories(false);
    }
  };

  // ============ ACCOUNTING YEAR START DATE ============
  const fetchAnneeComptable = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/club/annee-comptable`);
      setAnneeComptable(response.data.debut_annee_comptable);
      setIsDefaultAnneeComptable(response.data.is_default);
    } catch (error) {
      console.error('Error fetching accounting year config:', error);
    }
  };

  const handleSaveAnneeComptable = async () => {
    setSavingAnneeComptable(true);
    try {
      await axios.put(`${API_URL}/api/club/annee-comptable`, anneeComptable);
      setIsDefaultAnneeComptable(false);
      toast.success('Date de début d\'année comptable mise à jour');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSavingAnneeComptable(false);
    }
  };

  // CGU Functions
  const fetchCgu = async () => {
    setLoadingCgu(true);
    try {
      const response = await axios.get(`${API_URL}/api/cgu`);
      setCguContent(response.data.content || '');
      setCguVersion(response.data.version || '1.0');
      setCguUpdatedAt(response.data.updated_at);
      setCguIsDefault(response.data.is_default);
    } catch (error) {
      console.error('Error fetching CGU:', error);
    } finally {
      setLoadingCgu(false);
    }
  };

  const handleSaveCgu = async () => {
    if (!cguContent.trim()) {
      toast.error('Le contenu des CGU ne peut pas être vide');
      return;
    }
    
    setSavingCgu(true);
    try {
      await axios.put(`${API_URL}/api/cgu`, {
        content: cguContent,
        version: cguVersion
      });
      setCguIsDefault(false);
      setCguUpdatedAt(new Date().toISOString());
      toast.success('CGU mises à jour avec succès');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde des CGU'));
    } finally {
      setSavingCgu(false);
    }
  };

  const handleResetCguToDefault = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser les CGU au modèle par défaut ? Vos modifications seront perdues.')) {
      return;
    }
    
    setSavingCgu(true);
    try {
      const response = await axios.post(`${API_URL}/api/cgu/reset-default`);
      toast.success('CGU réinitialisées au modèle par défaut');
      fetchCgu(); // Reload the default content
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la réinitialisation'));
    } finally {
      setSavingCgu(false);
    }
  };

  const updateAgeCategoryField = (categoryName, field, value) => {
    setEditingAgeCategories(prev => ({
      ...prev,
      [categoryName]: {
        ...prev[categoryName],
        [field]: value === '' ? (field === 'max' ? null : 0) : parseInt(value, 10)
      }
    }));
  };

  const handleSaveTheme = async () => {
    if (!newTheme.nom) {
      toast.error('Le nom du thème est obligatoire');
      return;
    }
    
    setLoading(true);
    try {
      if (editingTheme) {
        await axios.put(`${API_URL}/api/themes/${editingTheme.id}`, newTheme);
        toast.success('Thème modifié');
      } else {
        await axios.post(`${API_URL}/api/themes`, newTheme);
        toast.success('Thème créé');
      }
      setShowThemesModal(false);
      setEditingTheme(null);
      setNewTheme({ nom: '', description: '', couleur: '#3b82f6' });
      fetchThemes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTheme = async (themeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce thème ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/themes/${themeId}`);
      toast.success('Thème supprimé');
      fetchThemes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  // Get current season based on current date
  const getCurrentSaison = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    // If September or later, it's the new season
    if (month >= 8) {
      return `${year}-${year + 1}`;
    }
    return `${year - 1}-${year}`;
  };

  const handleSaveVacances = async () => {
    if (!newVacances.nom || !newVacances.date_debut || !newVacances.date_fin) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    try {
      const dataToSend = {
        ...newVacances,
        saison: newVacances.saison || getCurrentSaison()
      };
      
      if (editingVacances) {
        await axios.put(`${API_URL}/api/vacances/${editingVacances.id}`, dataToSend);
        toast.success('Période de vacances modifiée');
      } else {
        await axios.post(`${API_URL}/api/vacances`, dataToSend);
        toast.success('Période de vacances créée');
      }
      
      setShowVacancesModal(false);
      setEditingVacances(null);
      setNewVacances({ nom: '', date_debut: '', date_fin: '', saison: '', notes: '' });
      fetchVacances();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleEditVacances = (v) => {
    setEditingVacances(v);
    setNewVacances({
      nom: v.nom,
      date_debut: v.date_debut.split('T')[0],
      date_fin: v.date_fin.split('T')[0],
      saison: v.saison,
      notes: v.notes || ''
    });
    setShowVacancesModal(true);
  };

  const handleDeleteVacances = async (id) => {
    if (!window.confirm('Supprimer cette période de vacances ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/vacances/${id}`);
      toast.success('Période supprimée');
      fetchVacances();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveProfile = async () => {
    // Validate required fields
    if (!profileData.date_naissance) {
      toast.error('La date de naissance est obligatoire');
      return;
    }
    
    setLoading(true);
    try {
      await updateProfile(profileData);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClub = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/club`, clubSettings);
      toast.success('Paramètres du club mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  // Logo upload handlers
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez JPG, PNG, WebP ou GIF.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/club/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setClubSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo téléchargé avec succès');
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Erreur lors du téléchargement');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Supprimer le logo du club ?')) return;

    setUploadingLogo(true);
    try {
      await axios.delete(`${API_URL}/api/club/logo`);
      setClubSettings(prev => ({ ...prev, logo_url: null }));
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Erreur lors de la suppression');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Maintenance functions
  const [runningMigration, setRunningMigration] = useState(null);
  const [migrationResults, setMigrationResults] = useState({});
  const [notificationsDisabled, setNotificationsDisabled] = useState(false);
  const [emailsDisabled, setEmailsDisabled] = useState(false);
  const [loadingNotifStatus, setLoadingNotifStatus] = useState(false);
  const [loadingEmailStatus, setLoadingEmailStatus] = useState(false);

  // Fetch notifications status
  useEffect(() => {
    if (isAdmin()) {
      fetchNotificationsStatus();
    }
  }, []);

  const fetchNotificationsStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications/status`);
      setNotificationsDisabled(response.data.notifications_disabled || false);
      setEmailsDisabled(response.data.emails_disabled || false);
    } catch (error) {
      console.error('Error fetching notifications status:', error);
    }
  };

  const handleToggleNotifications = async () => {
    const newValue = !notificationsDisabled;
    const action = newValue ? 'désactiver' : 'réactiver';
    
    if (!window.confirm(`Voulez-vous ${action} toutes les notifications (in-app et emails) ?`)) return;
    
    setLoadingNotifStatus(true);
    try {
      await axios.put(`${API_URL}/api/notifications/toggle`, { disabled: newValue });
      setNotificationsDisabled(newValue);
      toast.success(newValue ? 'Toutes les notifications désactivées' : 'Toutes les notifications réactivées');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du changement'));
    } finally {
      setLoadingNotifStatus(false);
    }
  };

  const handleToggleEmails = async () => {
    const newValue = !emailsDisabled;
    const action = newValue ? 'désactiver' : 'réactiver';
    
    if (!window.confirm(`Voulez-vous ${action} les envois d'emails ?`)) return;
    
    setLoadingEmailStatus(true);
    try {
      await axios.put(`${API_URL}/api/emails/toggle`, { disabled: newValue });
      setEmailsDisabled(newValue);
      toast.success(newValue ? 'Envois d\'emails désactivés' : 'Envois d\'emails réactivés');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du changement'));
    } finally {
      setLoadingEmailStatus(false);
    }
  };

  const handleMigrateRecurrenceIds = async () => {
    if (!window.confirm('Cette opération va ajouter des identifiants de récurrence aux cours hebdomadaires existants. Continuer ?')) return;
    
    setRunningMigration('recurrence');
    try {
      const response = await axios.post(`${API_URL}/api/migrate-recurrence-ids`);
      setMigrationResults(prev => ({ ...prev, recurrence: response.data }));
      toast.success(`Migration terminée : ${response.data.migrated_count} cours mis à jour`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la migration'));
    } finally {
      setRunningMigration(null);
    }
  };

  const handleMigrateInscriptions = async () => {
    if (!window.confirm('Cette opération va corriger les inscriptions et recalculer les places disponibles. Continuer ?')) return;
    
    setRunningMigration('inscriptions');
    try {
      const response = await axios.post(`${API_URL}/api/migrate-inscriptions`);
      setMigrationResults(prev => ({ ...prev, inscriptions: response.data }));
      toast.success(`Migration terminée : ${response.data.places_recalculated} cours recalculés`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la migration'));
    } finally {
      setRunningMigration(null);
    }
  };

  const handleDiagnosticDoublons = async () => {
    setRunningMigration('diagnostic');
    try {
      const response = await axios.get(`${API_URL}/api/diagnostic-doublons`);
      setMigrationResults(prev => ({ ...prev, diagnostic: response.data }));
      if (response.data.duplicate_groups > 0) {
        toast.warning(`${response.data.total_duplicates} doublons détectés dans ${response.data.duplicate_groups} groupes`);
      } else {
        toast.success('Aucun doublon détecté');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du diagnostic'));
    } finally {
      setRunningMigration(null);
    }
  };

  const handleCleanupDoublons = async () => {
    if (!window.confirm('⚠️ ATTENTION: Cette opération va supprimer définitivement les cours en doublon. Assurez-vous d\'avoir fait une sauvegarde. Continuer ?')) return;
    if (!window.confirm('Êtes-vous vraiment sûr ? Cette action est irréversible.')) return;
    
    setRunningMigration('cleanup');
    try {
      const response = await axios.post(`${API_URL}/api/cleanup-doublons`);
      setMigrationResults(prev => ({ ...prev, cleanup: response.data, diagnostic: null }));
      toast.success(`Nettoyage terminé : ${response.data.deleted_count} doublons supprimés`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors du nettoyage'));
    } finally {
      setRunningMigration(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const roleLabels = {
    cavalier_proprio: 'Cavalier propriétaire',
    cavalier_dp: 'Cavalier demi-pensionnaire',
    cavalier_forfait: 'Cavalier au forfait',
    cavalier_externe: 'Cavalier externe',
    instructeur: 'Instructeur',
    instructeur_admin: 'Administrateur'
  };

  return (
    <div className="animate-fadeIn" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D]">Paramètres</h1>
        <p className="text-stone-500 mt-1">Gérez votre compte et les paramètres</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="profile">
            <User className="w-4 h-4 mr-2" />
            Mon profil
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          {isAdmin() && (
            <TabsTrigger value="club">
              <Building className="w-4 h-4 mr-2" />
              Club
            </TabsTrigger>
          )}
          {isAdmin() && (
            <TabsTrigger value="inscriptions">
              <Calendar className="w-4 h-4 mr-2" />
              Inscriptions
            </TabsTrigger>
          )}
          {isAdmin() && (
            <TabsTrigger value="vacances">
              <Palmtree className="w-4 h-4 mr-2" />
              Vacances
            </TabsTrigger>
          )}
          {isAdmin() && (
            <TabsTrigger value="themes">
              <BookOpen className="w-4 h-4 mr-2" />
              Thèmes
            </TabsTrigger>
          )}
          {isAdmin() && (
            <TabsTrigger value="cgu">
              <FileText className="w-4 h-4 mr-2" />
              CGU
            </TabsTrigger>
          )}
          {isAdmin() && (
            <TabsTrigger value="maintenance">
              <Shield className="w-4 h-4 mr-2" />
              Maintenance
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* User info card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Informations personnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Prénom</Label>
                    <Input
                      value={profileData.prenom}
                      onChange={(e) => setProfileData({...profileData, prenom: e.target.value})}
                      className="input-field"
                      data-testid="profile-prenom"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Nom</Label>
                    <Input
                      value={profileData.nom}
                      onChange={(e) => setProfileData({...profileData, nom: e.target.value})}
                      className="input-field"
                      data-testid="profile-nom"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="form-label">Pseudo (surnom)</Label>
                  <Input
                    value={profileData.pseudo}
                    onChange={(e) => setProfileData({...profileData, pseudo: e.target.value})}
                    className="input-field"
                    placeholder="Ex: JD, Jojo..."
                    data-testid="profile-pseudo"
                  />
                </div>
                
                <div>
                  <Label className="form-label">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      value={profileData.telephone}
                      onChange={(e) => setProfileData({...profileData, telephone: e.target.value})}
                      className="input-field pl-10"
                      placeholder="06 12 34 56 78"
                      data-testid="profile-telephone"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="form-label">Date de naissance <span className="text-rose-500">*</span></Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      type="date"
                      value={profileData.date_naissance}
                      onChange={(e) => setProfileData({...profileData, date_naissance: e.target.value})}
                      className={`input-field pl-10 ${!profileData.date_naissance ? 'border-rose-300 focus:border-rose-500' : ''}`}
                      data-testid="profile-dob"
                      required
                    />
                  </div>
                  {!profileData.date_naissance && (
                    <p className="text-xs text-rose-500 mt-1">Ce champ est obligatoire</p>
                  )}
                </div>
                
                <div>
                  <Label className="form-label">Adresse</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-stone-400" />
                    <Textarea
                      value={profileData.adresse}
                      onChange={(e) => setProfileData({...profileData, adresse: e.target.value})}
                      className="input-field pl-10 min-h-[80px]"
                      placeholder="Votre adresse"
                      data-testid="profile-adresse"
                    />
                  </div>
                </div>
                
                <Button 
                  className="btn-primary w-full" 
                  onClick={handleSaveProfile}
                  disabled={loading}
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </CardContent>
            </Card>

            {/* Account info card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Mon compte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-stone-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-stone-400" />
                    <div>
                      <p className="text-sm text-stone-500">Email</p>
                      <p className="font-medium">{user?.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-stone-400" />
                    <div>
                      <p className="text-sm text-stone-500">Rôle</p>
                      <p className="font-medium">{roleLabels[user?.role_club] || user?.role_club}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-stone-400" />
                    <div>
                      <p className="text-sm text-stone-500">Statut</p>
                      <div className="flex items-center gap-2">
                        {user?.statut === 'actif' ? (
                          <span className="status-badge status-active">Actif</span>
                        ) : (
                          <span className="status-badge status-inactive">Inactif</span>
                        )}
                        {user?.is_approved === 'approuve' ? (
                          <span className="status-badge status-active">Approuvé</span>
                        ) : (
                          <span className="status-badge status-pending">En attente</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={handleLogout}
                    data-testid="logout-btn-settings"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Se déconnecter
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Préférences de notification
              </CardTitle>
              <p className="text-sm text-stone-500 mt-1">
                Configurez les notifications automatiques que vous souhaitez recevoir
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingNotifications && Object.keys(notificationPrefs).length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
              ) : (
                <>
                  {/* Header with icons */}
                  <div className="flex items-center justify-between p-3 bg-stone-100 rounded-lg text-sm font-medium">
                    <span>Type de notification</span>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-1 text-blue-600">
                        <Smartphone className="w-4 h-4" />
                        <span>In-app</span>
                      </div>
                      <div className="flex items-center gap-1 text-emerald-600">
                        <Mail className="w-4 h-4" />
                        <span>Email</span>
                      </div>
                    </div>
                  </div>

                  {/* Domain list */}
                  <div className="space-y-2">
                    {Object.entries(notificationDomains).map(([domain, config]) => {
                      const DomainIcon = {
                        cours_stages: Calendar,
                        facturation: Receipt,
                        tarifs: Euro,
                        chevaux: HeartPulse,
                        groupes: Users
                      }[domain] || Bell;
                      
                      const isExpanded = expandedDomains[domain];
                      
                      return (
                        <div key={domain} className="border rounded-lg overflow-hidden">
                          {/* Domain header - clickable to expand */}
                          <div 
                            className="flex items-center justify-between p-3 bg-stone-50 cursor-pointer hover:bg-stone-100 transition-colors"
                            onClick={() => toggleDomainExpanded(domain)}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-stone-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-stone-400" />
                              )}
                              <DomainIcon className="w-5 h-5 text-stone-600" />
                              <span className="font-medium">{config.label}</span>
                            </div>
                            <span className="text-xs text-stone-400">
                              {config.actions.length} {config.actions.length > 1 ? 'contextes' : 'contexte'}
                            </span>
                          </div>
                          
                          {/* Actions (expanded) */}
                          {isExpanded && (
                            <div className="divide-y">
                              {config.actions.map(action => {
                                const inAppChecked = notificationPrefs[domain]?.[action]?.in_app ?? true;
                                const emailChecked = notificationPrefs[domain]?.[action]?.email ?? false;
                                
                                return (
                                  <div 
                                    key={action}
                                    className="flex items-center justify-between p-3 pl-12 hover:bg-stone-50"
                                  >
                                    <span className="text-sm text-stone-700">
                                      {notificationActionLabels[action] || action}
                                    </span>
                                    <div className="flex items-center gap-8">
                                      {/* In-app toggle */}
                                      <Checkbox
                                        checked={inAppChecked}
                                        onCheckedChange={(checked) => toggleActionPref(domain, action, 'in_app', checked)}
                                        className="h-5 w-5 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                        data-testid={`notif-${domain}-${action}-inapp`}
                                      />
                                      {/* Email toggle */}
                                      <Checkbox
                                        checked={emailChecked}
                                        onCheckedChange={(checked) => toggleActionPref(domain, action, 'email', checked)}
                                        className="h-5 w-5 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                                        data-testid={`notif-${domain}-${action}-email`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Save button */}
                  <Button 
                    className="btn-primary w-full" 
                    onClick={handleSaveNotificationPrefs}
                    disabled={loadingNotifications}
                    data-testid="save-notifications-btn"
                  >
                    {loadingNotifications ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer les préférences
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Club Tab (Admin only) */}
        {isAdmin() && (
          <TabsContent value="club">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Informations du club
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {clubSettings ? (
                  <>
                    {/* Logo Section */}
                    <div className="pb-4 border-b">
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        Logo du club
                      </h3>
                      <p className="text-sm text-stone-500 mb-4">
                        Le logo apparaîtra dans les emails envoyés aux cavaliers
                      </p>
                      
                      <div className="flex items-center gap-6">
                        {/* Logo preview */}
                        <div className="relative">
                          {clubSettings.logo_url ? (
                            <div className="relative group">
                              <img
                                src={resolveUrl(clubSettings.logo_url)}
                                alt="Logo du club"
                                className="w-24 h-24 object-contain rounded-lg border bg-white p-1"
                                data-testid="club-logo-preview"
                              />
                              <button
                                onClick={handleDeleteLogo}
                                disabled={uploadingLogo}
                                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                title="Supprimer le logo"
                                data-testid="delete-logo-btn"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-24 h-24 flex items-center justify-center bg-stone-100 border-2 border-dashed border-stone-300 rounded-lg">
                              <ImageIcon className="w-8 h-8 text-stone-400" />
                            </div>
                          )}
                        </div>

                        {/* Upload button */}
                        <div className="flex flex-col gap-2">
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={handleLogoUpload}
                            className="hidden"
                            id="logo-upload"
                            data-testid="logo-file-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                            data-testid="upload-logo-btn"
                            className="gap-2"
                          >
                            <Upload className="w-4 h-4" />
                            {uploadingLogo ? 'Téléchargement...' : 'Télécharger un logo'}
                          </Button>
                          <p className="text-xs text-stone-500">
                            JPG, PNG, WebP ou GIF (max 5MB)
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="form-label">Nom du club</Label>
                        <Input
                          value={clubSettings.nom || ''}
                          onChange={(e) => setClubSettings({...clubSettings, nom: e.target.value})}
                          className="input-field"
                          data-testid="club-name"
                        />
                      </div>
                      
                      <div>
                        <Label className="form-label">Email de contact</Label>
                        <Input
                          type="email"
                          value={clubSettings.email || ''}
                          onChange={(e) => setClubSettings({...clubSettings, email: e.target.value})}
                          className="input-field"
                          data-testid="club-email"
                        />
                      </div>
                      
                      <div>
                        <Label className="form-label">Téléphone</Label>
                        <Input
                          value={clubSettings.telephone || ''}
                          onChange={(e) => setClubSettings({...clubSettings, telephone: e.target.value})}
                          className="input-field"
                          data-testid="club-telephone"
                        />
                      </div>
                      
                      <div>
                        <Label className="form-label">Site web</Label>
                        <Input
                          value={clubSettings.site_web || ''}
                          onChange={(e) => setClubSettings({...clubSettings, site_web: e.target.value})}
                          className="input-field"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t">
                      <h3 className="font-medium mb-3">Adresse</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label className="form-label">Rue</Label>
                          <Input
                            value={clubSettings.adresse_rue || ''}
                            onChange={(e) => setClubSettings({...clubSettings, adresse_rue: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Code postal</Label>
                          <Input
                            value={clubSettings.adresse_cp || ''}
                            onChange={(e) => setClubSettings({...clubSettings, adresse_cp: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Ville</Label>
                          <Input
                            value={clubSettings.adresse_ville || ''}
                            onChange={(e) => setClubSettings({...clubSettings, adresse_ville: e.target.value})}
                            className="input-field"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t">
                      <h3 className="font-medium mb-3">Informations légales</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="form-label">SIRET</Label>
                          <Input
                            value={clubSettings.siret || ''}
                            onChange={(e) => setClubSettings({...clubSettings, siret: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Forme juridique</Label>
                          <Input
                            value={clubSettings.forme_juridique || ''}
                            onChange={(e) => setClubSettings({...clubSettings, forme_juridique: e.target.value})}
                            className="input-field"
                            placeholder="Association, SARL, EARL..."
                          />
                        </div>
                        <div>
                          <Label className="form-label">Capital (€)</Label>
                          <Input
                            value={clubSettings.capital || ''}
                            onChange={(e) => setClubSettings({...clubSettings, capital: e.target.value})}
                            className="input-field"
                            placeholder="Ex: 10000"
                          />
                        </div>
                        <div>
                          <Label className="form-label">TVA intracommunautaire</Label>
                          <Input
                            value={clubSettings.tva_intra || ''}
                            onChange={(e) => setClubSettings({...clubSettings, tva_intra: e.target.value})}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Code APE</Label>
                          <Input
                            value={clubSettings.code_ape || ''}
                            onChange={(e) => setClubSettings({...clubSettings, code_ape: e.target.value})}
                            className="input-field"
                            placeholder="Ex: 9312Z"
                          />
                        </div>
                        <div>
                          <Label className="form-label">RCS (ville du greffe)</Label>
                          <Input
                            value={clubSettings.rcs_ville || ''}
                            onChange={(e) => setClubSettings({...clubSettings, rcs_ville: e.target.value})}
                            className="input-field"
                            placeholder="Ex: Paris"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t">
                      <h3 className="font-medium mb-3">Coordonnées bancaires</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label className="form-label">IBAN</Label>
                          <Input
                            value={clubSettings.iban || ''}
                            onChange={(e) => setClubSettings({...clubSettings, iban: e.target.value})}
                            className="input-field font-mono"
                            placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                          />
                        </div>
                        <div>
                          <Label className="form-label">BIC</Label>
                          <Input
                            value={clubSettings.bic || ''}
                            onChange={(e) => setClubSettings({...clubSettings, bic: e.target.value})}
                            className="input-field font-mono"
                            placeholder="XXXXXXXX"
                          />
                        </div>
                        <div>
                          <Label className="form-label">Nom de la banque</Label>
                          <Input
                            value={clubSettings.banque_nom || ''}
                            onChange={(e) => setClubSettings({...clubSettings, banque_nom: e.target.value})}
                            className="input-field"
                            placeholder="Ex: Crédit Agricole"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Préférences de facturation */}
                    <div className="pt-4 border-t">
                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <Receipt className="w-4 h-4" />
                        Préférences de facturation
                      </h3>
                      <div className="space-y-6">
                        {/* Module Facturation (optionnel) */}
                        <div className="p-4 bg-stone-50 rounded-lg border">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Receipt className="w-5 h-5 text-[#2E4A3D]" />
                                <span className="font-medium">Module Facturation</span>
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Optionnel</span>
                              </div>
                              <p className="text-sm text-stone-500">
                                Active l'onglet Facturation pour gérer les factures, avoirs et paiements.
                                Sans ce module, seuls les onglets Décompte et Tarifs sont disponibles.
                              </p>
                            </div>
                            <div className="ml-4">
                              <button
                                type="button"
                                onClick={() => setClubSettings({
                                  ...clubSettings, 
                                  facturation_enabled: !clubSettings.facturation_enabled,
                                  // Reset default view if disabling facturation and it was the default
                                  default_billing_view: !clubSettings.facturation_enabled ? clubSettings.default_billing_view : 
                                    (clubSettings.default_billing_view === 'facturation' ? 'decompte' : clubSettings.default_billing_view)
                                })}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  clubSettings.facturation_enabled ? 'bg-[#2E4A3D]' : 'bg-stone-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    clubSettings.facturation_enabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Onglet par défaut */}
                        <div>
                          <Label className="form-label mb-2">Onglet par défaut</Label>
                          <p className="text-sm text-stone-500 mb-3">
                            Choisissez l'onglet affiché par défaut dans la page "Tarifs et facturation"
                          </p>
                          <div className={`grid grid-cols-1 gap-3 ${clubSettings.facturation_enabled ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                            {/* Facturation - only if enabled */}
                            {clubSettings.facturation_enabled && (
                              <button
                                type="button"
                                onClick={() => setClubSettings({...clubSettings, default_billing_view: 'facturation'})}
                                className={`p-4 rounded-lg border-2 transition-all text-left ${
                                  clubSettings.default_billing_view === 'facturation'
                                    ? 'border-[#2E4A3D] bg-[#2E4A3D]/5'
                                    : 'border-stone-200 hover:border-stone-300'
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Receipt className="w-5 h-5 text-[#2E4A3D]" />
                                  <span className="font-medium">Facturation</span>
                                </div>
                                <p className="text-sm text-stone-500">
                                  Factures, avoirs, paiements
                                </p>
                              </button>
                            )}
                            
                            <button
                              type="button"
                              onClick={() => setClubSettings({...clubSettings, default_billing_view: 'decompte'})}
                              className={`p-4 rounded-lg border-2 transition-all text-left ${
                                (clubSettings.default_billing_view === 'decompte' || (!clubSettings.facturation_enabled && !clubSettings.default_billing_view))
                                  ? 'border-[#2E4A3D] bg-[#2E4A3D]/5'
                                  : 'border-stone-200 hover:border-stone-300'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="w-5 h-5 text-[#2E4A3D]" />
                                <span className="font-medium">Décompte</span>
                              </div>
                              <p className="text-sm text-stone-500">
                                Suivi détaillé par cavalier
                              </p>
                            </button>
                            
                            <button
                              type="button"
                              onClick={() => setClubSettings({...clubSettings, default_billing_view: 'tarifs'})}
                              className={`p-4 rounded-lg border-2 transition-all text-left ${
                                clubSettings.default_billing_view === 'tarifs'
                                  ? 'border-[#2E4A3D] bg-[#2E4A3D]/5'
                                  : 'border-stone-200 hover:border-stone-300'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Euro className="w-5 h-5 text-[#2E4A3D]" />
                                <span className="font-medium">Tarifs</span>
                              </div>
                              <p className="text-sm text-stone-500">
                                Barèmes tarifaires
                              </p>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      className="btn-primary w-full" 
                      onClick={handleSaveClub}
                      disabled={loading}
                      data-testid="save-club-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Enregistrement...' : 'Enregistrer les paramètres'}
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="spinner mx-auto" />
                    <p className="text-stone-500 mt-4">Chargement des paramètres...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Inscriptions Tab - Age Categories */}
        {isAdmin() && (
          <TabsContent value="inscriptions">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Règles d'inscription aux cours
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Info box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  <p className="font-medium mb-2">Règles d'inscription :</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Les cavaliers ne peuvent pas s'inscrire eux-mêmes aux cours hebdomadaires récurrents réservés à un groupe</li>
                    <li>L'inscription aux cours ponctuels et stages est limitée par la catégorie d'âge et le niveau de galop (si définis)</li>
                  </ul>
                </div>

                {/* Age Categories */}
                <div>
                  <h3 className="font-medium mb-4">Catégories d'âge</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Définissez les tranches d'âge pour chaque catégorie. Ces catégories sont utilisées pour filtrer les inscriptions aux cours.
                  </p>
                  
                  {editingAgeCategories && (
                    <div className="space-y-4">
                      {Object.entries(editingAgeCategories).map(([categoryName, config]) => (
                        <div key={categoryName} className="grid grid-cols-3 gap-4 items-center p-3 bg-gray-50 rounded-lg">
                          <div className="font-medium">{categoryName}</div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground">Min:</Label>
                            <Input
                              type="number"
                              min="0"
                              max="99"
                              value={config.min || 0}
                              onChange={(e) => updateAgeCategoryField(categoryName, 'min', e.target.value)}
                              className="w-20"
                              data-testid={`age-cat-${categoryName.toLowerCase().replace(/\s+/g, '-')}-min`}
                            />
                            <span className="text-sm text-muted-foreground">ans</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground">Max:</Label>
                            <Input
                              type="number"
                              min="0"
                              max="99"
                              value={config.max ?? ''}
                              placeholder="∞"
                              onChange={(e) => updateAgeCategoryField(categoryName, 'max', e.target.value)}
                              className="w-20"
                              data-testid={`age-cat-${categoryName.toLowerCase().replace(/\s+/g, '-')}-max`}
                            />
                            <span className="text-sm text-muted-foreground">ans</span>
                          </div>
                        </div>
                      ))}
                      
                      <div className="flex gap-3 mt-6">
                        <Button
                          onClick={handleSaveAgeCategories}
                          disabled={savingAgeCategories}
                          className="btn-primary"
                          data-testid="save-age-categories-btn"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {savingAgeCategories ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleResetAgeCategories}
                          disabled={savingAgeCategories}
                          data-testid="reset-age-categories-btn"
                        >
                          Réinitialiser par défaut
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Accounting Year Start Date */}
                <div className="pt-6 border-t">
                  <h3 className="font-medium mb-4">Année comptable</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Définissez la date de début de votre année comptable. Cette date est utilisée pour la numérotation des factures et les exports comptables.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <Label className="text-xs text-muted-foreground">Mois</Label>
                      <Select 
                        value={String(anneeComptable.mois)} 
                        onValueChange={(v) => setAnneeComptable({ ...anneeComptable, mois: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="accounting-year-month">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Janvier</SelectItem>
                          <SelectItem value="2">Février</SelectItem>
                          <SelectItem value="3">Mars</SelectItem>
                          <SelectItem value="4">Avril</SelectItem>
                          <SelectItem value="5">Mai</SelectItem>
                          <SelectItem value="6">Juin</SelectItem>
                          <SelectItem value="7">Juillet</SelectItem>
                          <SelectItem value="8">Août</SelectItem>
                          <SelectItem value="9">Septembre</SelectItem>
                          <SelectItem value="10">Octobre</SelectItem>
                          <SelectItem value="11">Novembre</SelectItem>
                          <SelectItem value="12">Décembre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Jour</Label>
                      <Select 
                        value={String(anneeComptable.jour)} 
                        onValueChange={(v) => setAnneeComptable({ ...anneeComptable, jour: parseInt(v) })}
                      >
                        <SelectTrigger data-testid="accounting-year-day">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 28 }, (_, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {isDefaultAnneeComptable && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Par défaut : 1er Janvier (année civile)
                    </p>
                  )}
                  
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={handleSaveAnneeComptable}
                      disabled={savingAnneeComptable}
                      className="btn-primary"
                      data-testid="save-accounting-year-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingAnneeComptable ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                    {!isDefaultAnneeComptable && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setAnneeComptable({ mois: 1, jour: 1 });
                          handleSaveAnneeComptable();
                        }}
                        disabled={savingAnneeComptable}
                      >
                        Réinitialiser (1er Janvier)
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* Vacances Tab */}
        {isAdmin() && (
          <TabsContent value="vacances">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palmtree className="w-5 h-5" />
                    Périodes de vacances
                  </CardTitle>
                  <Button
                    onClick={() => {
                      setEditingVacances(null);
                      setNewVacances({ 
                        nom: '', 
                        date_debut: '', 
                        date_fin: '', 
                        saison: getCurrentSaison(), 
                        notes: '' 
                      });
                      setShowVacancesModal(true);
                    }}
                    className="btn-primary"
                    data-testid="add-vacances-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  Les périodes de vacances sont exclues lors de la création en masse de cours hebdomadaires.
                </p>
              </CardHeader>
              <CardContent>
                {vacances.length === 0 ? (
                  <div className="text-center py-8">
                    <Palmtree className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <p className="text-stone-500">Aucune période de vacances définie</p>
                    <p className="text-sm text-stone-400 mt-1">
                      Ajoutez les vacances scolaires pour la saison {getCurrentSaison()}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Group by season */}
                    {Object.entries(
                      vacances.reduce((acc, v) => {
                        if (!acc[v.saison]) acc[v.saison] = [];
                        acc[v.saison].push(v);
                        return acc;
                      }, {})
                    ).sort((a, b) => b[0].localeCompare(a[0])).map(([saison, items]) => (
                      <div key={saison} className="mb-6">
                        <h3 className="text-sm font-semibold text-stone-600 mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Saison {saison}
                        </h3>
                        <div className="space-y-2">
                          {items.map((v) => (
                            <div 
                              key={v.id} 
                              className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                              data-testid={`vacances-${v.id}`}
                            >
                              <div>
                                <p className="font-medium text-stone-800">{v.nom}</p>
                                <p className="text-sm text-stone-600">
                                  {format(new Date(v.date_debut), 'd MMM', { locale: fr })} - {format(new Date(v.date_fin), 'd MMM yyyy', { locale: fr })}
                                </p>
                                {v.notes && (
                                  <p className="text-xs text-stone-500 mt-1">{v.notes}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditVacances(v)}
                                  data-testid={`edit-vacances-${v.id}`}
                                >
                                  <Edit2 className="w-4 h-4 text-stone-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteVacances(v.id)}
                                  data-testid={`delete-vacances-${v.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Vacances Modal */}
            <Dialog open={showVacancesModal} onOpenChange={setShowVacancesModal}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif">
                    {editingVacances ? 'Modifier la période' : 'Nouvelle période de vacances'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="form-label">Nom *</Label>
                    <Input
                      value={newVacances.nom}
                      onChange={(e) => setNewVacances({...newVacances, nom: e.target.value})}
                      placeholder="Ex: Vacances de Noël"
                      className="input-field"
                      data-testid="vacances-nom-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Date début *</Label>
                      <Input
                        type="date"
                        value={newVacances.date_debut}
                        onChange={(e) => setNewVacances({...newVacances, date_debut: e.target.value})}
                        className="input-field"
                        data-testid="vacances-debut-input"
                      />
                    </div>
                    <div>
                      <Label className="form-label">Date fin *</Label>
                      <Input
                        type="date"
                        value={newVacances.date_fin}
                        onChange={(e) => setNewVacances({...newVacances, date_fin: e.target.value})}
                        className="input-field"
                        data-testid="vacances-fin-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="form-label">Saison</Label>
                    <Input
                      value={newVacances.saison}
                      onChange={(e) => setNewVacances({...newVacances, saison: e.target.value})}
                      placeholder="Ex: 2025-2026"
                      className="input-field"
                      data-testid="vacances-saison-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="form-label">Notes</Label>
                    <Textarea
                      value={newVacances.notes}
                      onChange={(e) => setNewVacances({...newVacances, notes: e.target.value})}
                      placeholder="Notes optionnelles..."
                      className="input-field"
                      rows={2}
                      data-testid="vacances-notes-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setShowVacancesModal(false);
                        setEditingVacances(null);
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      className="flex-1 btn-primary"
                      onClick={handleSaveVacances}
                      disabled={loading}
                      data-testid="save-vacances-btn"
                    >
                      {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Themes Tab */}
        {isAdmin() && (
          <TabsContent value="themes">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Thèmes d&apos;enseignement
                  </CardTitle>
                  <Button
                    onClick={() => {
                      setEditingTheme(null);
                      setNewTheme({ nom: '', description: '', couleur: '#3b82f6' });
                      setShowThemesModal(true);
                    }}
                    className="btn-primary"
                    data-testid="add-theme-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  Les thèmes permettent de catégoriser les séances et suivre la progression pédagogique.
                </p>
              </CardHeader>
              <CardContent>
                {themes.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <p className="text-stone-500">Aucun thème d&apos;enseignement défini</p>
                    <p className="text-sm text-stone-400 mt-1">
                      Créez des thèmes pour organiser vos séances (ex: Dressage, Obstacle, Balades...)
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {themes.map((theme) => (
                      <div
                        key={theme.id}
                        className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: theme.couleur || '#3b82f6' }}
                            />
                            <div>
                              <h3 className="font-medium text-stone-800">{theme.nom}</h3>
                              {theme.description && (
                                <p className="text-sm text-stone-500 mt-1">{theme.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingTheme(theme);
                                setNewTheme({
                                  nom: theme.nom,
                                  description: theme.description || '',
                                  couleur: theme.couleur || '#3b82f6'
                                });
                                setShowThemesModal(true);
                              }}
                              className="h-8 w-8 p-0"
                            >
                              <Edit2 className="w-4 h-4 text-stone-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTheme(theme.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Theme Modal */}
            <Dialog open={showThemesModal} onOpenChange={setShowThemesModal}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingTheme ? 'Modifier le thème' : 'Nouveau thème d\'enseignement'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label className="form-label">Nom du thème *</Label>
                    <Input
                      value={newTheme.nom}
                      onChange={(e) => setNewTheme({ ...newTheme, nom: e.target.value })}
                      placeholder="Ex: Dressage, Obstacle, Voltige..."
                      className="input-field"
                      data-testid="theme-nom-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Description</Label>
                    <Textarea
                      value={newTheme.description}
                      onChange={(e) => setNewTheme({ ...newTheme, description: e.target.value })}
                      placeholder="Description optionnelle du thème..."
                      className="input-field"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label className="form-label">Couleur</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={newTheme.couleur}
                        onChange={(e) => setNewTheme({ ...newTheme, couleur: e.target.value })}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <span className="text-sm text-stone-500">
                        Cette couleur sera utilisée pour identifier le thème dans le planning
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowThemesModal(false)}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button
                      className="btn-primary flex-1"
                      onClick={handleSaveTheme}
                      disabled={loading}
                      data-testid="save-theme-btn"
                    >
                      {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* CGU Tab */}
        {isAdmin() && (
          <TabsContent value="cgu">
            <div className="card p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#2E4A3D] flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Conditions Générales d'Utilisation
                  </h2>
                  <p className="text-sm text-stone-500 mt-1">
                    Personnalisez les CGU que les utilisateurs doivent accepter lors de l'inscription
                  </p>
                </div>
                {cguIsDefault && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Modèle par défaut
                  </span>
                )}
              </div>

              {loadingCgu ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2E4A3D]" />
                </div>
              ) : (
                <>
                  {/* Version & Info */}
                  <div className="grid md:grid-cols-3 gap-4 p-4 bg-stone-50 rounded-lg">
                    <div>
                      <Label className="form-label">Version</Label>
                      <Input
                        value={cguVersion}
                        onChange={(e) => setCguVersion(e.target.value)}
                        placeholder="1.0"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <Label className="form-label">Dernière mise à jour</Label>
                      <p className="text-sm text-stone-600 mt-2">
                        {cguUpdatedAt 
                          ? new Date(cguUpdatedAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'Jamais'}
                      </p>
                    </div>
                    <div className="flex items-end">
                      <Button
                        variant="outline"
                        onClick={handleResetCguToDefault}
                        disabled={savingCgu || cguIsDefault}
                        className="gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Réinitialiser au modèle
                      </Button>
                    </div>
                  </div>

                  {/* Editor */}
                  <div>
                    <Label className="form-label mb-2 block">
                      Contenu des CGU (format Markdown)
                    </Label>
                    <textarea
                      value={cguContent}
                      onChange={(e) => setCguContent(e.target.value)}
                      className="w-full h-[500px] p-4 font-mono text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-[#2E4A3D]/20 focus:border-[#2E4A3D] resize-y"
                      placeholder="# CONDITIONS GÉNÉRALES D'UTILISATION&#10;&#10;Écrivez vos CGU en format Markdown..."
                    />
                    <p className="text-xs text-stone-500 mt-2">
                      Utilisez le format Markdown : # pour les titres, ** pour le gras, * pour l'italique, --- pour les séparateurs
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 justify-end pt-4 border-t">
                    <Button
                      onClick={handleSaveCgu}
                      disabled={savingCgu}
                      className="btn-primary gap-2"
                      data-testid="save-cgu-btn"
                    >
                      {savingCgu ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Enregistrer les CGU
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        )}

        {/* Maintenance Tab */}
        {isAdmin() && (
          <TabsContent value="maintenance">
            <div className="card p-6 space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-[#2E4A3D] flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Maintenance & Migrations
                </h2>
                <p className="text-sm text-stone-500 mt-1">
                  Outils de maintenance pour corriger les données de l'application
                </p>
              </div>

              {/* Notifications Toggle */}
              <div className={`p-4 rounded-lg border ${notificationsDisabled ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-stone-800 flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Désactiver toutes les notifications
                    </h3>
                    <p className="text-sm text-stone-600 mt-1">
                      {notificationsDisabled 
                        ? "⚠️ Toutes les notifications sont désactivées. Les cavaliers ne reçoivent plus aucune notification (ni in-app, ni par email) concernant les nouveaux cours, stages, modifications, annulations ou rappels."
                        : "Les notifications sont actives. Les cavaliers reçoivent des notifications in-app et par email selon leurs préférences individuelles."
                      }
                    </p>
                    <ul className="text-xs text-stone-500 mt-2 space-y-1 list-disc list-inside">
                      <li>Notifications in-app : alertes dans l'application (cloche)</li>
                      <li>Notifications email : envoi d'emails pour les événements importants</li>
                      <li>Utile en cas de maintenance ou de période de fermeture du club</li>
                    </ul>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-sm font-medium ${notificationsDisabled ? 'text-amber-700' : 'text-green-700'}`}>
                      {notificationsDisabled ? 'Désactivées' : 'Activées'}
                    </span>
                    <Button
                      onClick={handleToggleNotifications}
                      disabled={loadingNotifStatus}
                      variant={notificationsDisabled ? 'default' : 'outline'}
                      className={notificationsDisabled ? 'bg-green-600 hover:bg-green-700' : 'border-amber-300 text-amber-700 hover:bg-amber-100'}
                    >
                      {loadingNotifStatus ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : notificationsDisabled ? (
                        'Réactiver'
                      ) : (
                        'Désactiver'
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Emails Toggle */}
              <div className={`p-4 rounded-lg border ${emailsDisabled ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-stone-800 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Désactiver les envois d'email
                    </h3>
                    <p className="text-sm text-stone-600 mt-1">
                      {emailsDisabled 
                        ? "⚠️ Les envois d'emails sont désactivés. Les cavaliers continuent de recevoir les notifications in-app, mais aucun email n'est envoyé (nouveaux cours, rappels, confirmations d'inscription, etc.)."
                        : "Les emails sont actifs. Les cavaliers reçoivent des emails selon leurs préférences pour : nouveaux cours/stages, modifications, annulations, rappels, confirmations."
                      }
                    </p>
                    <ul className="text-xs text-stone-500 mt-2 space-y-1 list-disc list-inside">
                      <li>Les notifications in-app restent fonctionnelles</li>
                      <li>Utile si problème avec le service d'envoi d'emails (Brevo)</li>
                      <li>Permet de tester les fonctionnalités sans envoyer de vrais emails</li>
                    </ul>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-sm font-medium ${emailsDisabled ? 'text-amber-700' : 'text-green-700'}`}>
                      {emailsDisabled ? 'Désactivés' : 'Activés'}
                    </span>
                    <Button
                      onClick={handleToggleEmails}
                      disabled={loadingEmailStatus || notificationsDisabled}
                      variant={emailsDisabled ? 'default' : 'outline'}
                      className={emailsDisabled ? 'bg-green-600 hover:bg-green-700' : 'border-amber-300 text-amber-700 hover:bg-amber-100'}
                      title={notificationsDisabled ? "Réactivez d'abord les notifications" : ""}
                    >
                      {loadingEmailStatus ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : emailsDisabled ? (
                        'Réactiver'
                      ) : (
                        'Désactiver'
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Migration: Recurrence IDs */}
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-stone-800">Migration des cours récurrents</h3>
                    <p className="text-sm text-stone-600 mt-1">
                      Ajoute un identifiant de récurrence aux cours hebdomadaires existants. 
                      Cela permet d'appliquer les modifications à tous les cours suivants d'une série.
                    </p>
                    {migrationResults.recurrence && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                        ✓ {migrationResults.recurrence.migrated_count} cours migrés 
                        ({migrationResults.recurrence.series_count} séries identifiées)
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleMigrateRecurrenceIds}
                    disabled={runningMigration !== null}
                    variant="outline"
                    className="shrink-0"
                  >
                    {runningMigration === 'recurrence' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        En cours...
                      </>
                    ) : (
                      'Exécuter'
                    )}
                  </Button>
                </div>
              </div>

              {/* Migration: Inscriptions & Places */}
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-stone-800">Recalcul des places disponibles</h3>
                    <p className="text-sm text-stone-600 mt-1">
                      Corrige le format des inscriptions et recalcule le nombre de places disponibles 
                      pour tous les cours (en tenant compte des groupes et inscriptions ponctuelles).
                    </p>
                    {migrationResults.inscriptions && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                        ✓ {migrationResults.inscriptions.places_recalculated} cours recalculés
                        {migrationResults.inscriptions.legacy_inscriptions_migrated > 0 && (
                          <>, {migrationResults.inscriptions.legacy_inscriptions_migrated} inscriptions migrées</>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleMigrateInscriptions}
                    disabled={runningMigration !== null}
                    variant="outline"
                    className="shrink-0"
                  >
                    {runningMigration === 'inscriptions' ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        En cours...
                      </>
                    ) : (
                      'Exécuter'
                    )}
                  </Button>
                </div>
              </div>

              {/* Diagnostic & Cleanup Doublons */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-red-800">🔍 Diagnostic & Nettoyage des doublons</h3>
                    <p className="text-sm text-red-700 mt-1">
                      Détecte et supprime les cours en doublon (même date, heure, thème, instructeur).
                      <strong> Faites d'abord un diagnostic avant le nettoyage.</strong>
                    </p>
                    {migrationResults.diagnostic && (
                      <div className="mt-2 p-2 bg-white rounded text-sm">
                        <p className="font-medium text-stone-800">
                          Résultat du diagnostic :
                        </p>
                        <ul className="mt-1 text-stone-600">
                          <li>• Total cours : {migrationResults.diagnostic.total_courses}</li>
                          <li>• Groupes en doublon : {migrationResults.diagnostic.duplicate_groups}</li>
                          <li className="text-red-600 font-medium">
                            • Doublons à supprimer : {migrationResults.diagnostic.total_duplicates}
                          </li>
                        </ul>
                      </div>
                    )}
                    {migrationResults.cleanup && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                        ✓ {migrationResults.cleanup.deleted_count} doublons supprimés
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      onClick={handleDiagnosticDoublons}
                      disabled={runningMigration !== null}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-100"
                    >
                      {runningMigration === 'diagnostic' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyse...
                        </>
                      ) : (
                        'Diagnostic'
                      )}
                    </Button>
                    <Button
                      onClick={handleCleanupDoublons}
                      disabled={runningMigration !== null || !migrationResults.diagnostic || migrationResults.diagnostic.total_duplicates === 0}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {runningMigration === 'cleanup' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Nettoyage...
                        </>
                      ) : (
                        'Nettoyer'
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Note :</strong> Les migrations sont sécurisées et peuvent être exécutées plusieurs fois. 
                  Le nettoyage des doublons est <strong>irréversible</strong> - faites une sauvegarde avant.
                </p>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
